import React, { useState, useEffect, useRef } from 'react';
import { STREETS } from '../constants';
import { useI18n } from '../hooks/useI18n';

const ACTION_COLORS = {
  bet: 'bg-blue-100',
  raise: 'bg-red-100',
  call: 'bg-green-100',
  check: 'bg-yellow-100',
  fold: 'bg-gray-100',
  open: 'bg-purple-100',
  limp: 'bg-emerald-100'
};

const ACTION_DOT_COLORS = {
  bet: 'bg-blue-500',
  raise: 'bg-red-500',
  call: 'bg-green-500',
  check: 'bg-yellow-500',
  fold: 'bg-gray-500',
  open: 'bg-purple-500',
  limp: 'bg-emerald-500'
};

const ActionItem = ({ action, isLatest, forceReset, street }) => {
  const { t, noTranslate } = useI18n();
  const [highlight, setHighlight] = useState(isLatest);
  const itemRef = useRef(null); 
  const timerRef = useRef(null);

  // 处理高亮效果
  useEffect(() => {
    // 清除之前的计时器
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    
    if (isLatest) {
      // 启动高亮效果
      setHighlight(true);
      // 2秒后结束高亮
      timerRef.current = setTimeout(() => {
        setHighlight(false);
        timerRef.current = null;
      }, 2000);
    } else {
      // 如果不是最新项，确保不高亮
      setHighlight(false);
    }
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isLatest, forceReset]);

  // 获取当前动作在当前街道中的索引
  const getActionColor = (actionType) => ACTION_COLORS[actionType] || 'bg-gray-100';
  const getDotColor = (actionType) => ACTION_DOT_COLORS[actionType] || 'bg-gray-500';

  // 确定显示的动作文本 - 首字母大写
  const displayAction = t(`poker.actions.${action.action.toLowerCase()}`);

  return (
    <div className="relative" ref={itemRef}>
      <div className={`p-2 rounded-md ${getActionColor(action.action)} 
        transition-all duration-300 ${
        highlight ? 'border-2 border-blue-500 shadow-md' : 'border border-transparent'
      }`}>
        <div className="flex items-center">
          <span className={`inline-block w-3 h-3 rounded-full mr-2 ${getDotColor(action.action)}`}></span>
          <span className="font-medium">{noTranslate(action.player)}</span>
          <span className="ml-1">: {displayAction}</span>
          {action.amount > 0 && <span className="ml-1">{action.amount}</span>}
          {action.potSize && <span className="ml-1 text-gray-600 text-sm">(Pot: {action.potSize})</span>}
        </div>
      </div>
      {highlight && (
        <div className="absolute -inset-1 bg-blue-200 opacity-30 rounded-md animate-pulse -z-10" />
      )}
    </div>
  );
};

const RecentActionSummary = ({ actions, latestAction, forceResetCounter }) => {
  const { t } = useI18n();
  // 获取所有街道的最近动作
  const recentActions = [];
  
  // 从每条街道收集动作
  STREETS.forEach(street => {
    if (actions[street] && actions[street].length > 0) {
      // 获取每条街道的最后两个动作
      const streetActions = actions[street].slice(-2);
      recentActions.push(...streetActions.map(action => ({...action, street})));
    }
  });
  
  // 只显示最近的3个动作
  const displayedActions = recentActions.slice(-3);
  
  if (displayedActions.length === 0) {
    return (
      <div className="text-gray-500 italic py-2 text-center">
        {t('poker.sections.noActions')}
      </div>
    );
  }
  
  return (
    <div className="space-y-1">
      <p className="text-xs text-gray-500 mb-1">{t('poker.sections.recentActions')}:</p>
      {displayedActions.map((action, index) => (
        <ActionItem
          key={`recent-${index}-${action.player}-${action.timestamp || index}`}
          action={action}
          isLatest={latestAction && action.timestamp === latestAction.timestamp}
          forceReset={forceResetCounter}
          street={action.street}
        />
      ))}
      <div className="text-center mt-2">
        <span className="text-xs text-gray-400">
          {t('poker.sections.totalActions', { count: recentActions.length })}
        </span>
      </div>
    </div>
  );
};

const ActionHistory = ({ actions, gameInfo, gameOver, latestAction, compactMode = false }) => {
  const { t } = useI18n();
  const containerRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [lastActionTimestamp, setLastActionTimestamp] = useState(null);
  const [forceResetCounter, setForceResetCounter] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const handleScroll = () => {
    if (containerRef.current) {
      setScrollPosition(containerRef.current.scrollTop);
      if (!isScrolling) {
        setIsScrolling(true);
        setTimeout(() => setIsScrolling(false), 300);
      }
    }
  };
  
  const lastActiveStreet = STREETS.reduce((last, street) => {
    return actions[street] && actions[street].length > 0 ? street : last;
  }, null);
  
  // 当有新动作添加时自动滚动并重置所有高亮
  useEffect(() => {
    if (latestAction && latestAction.timestamp !== lastActionTimestamp) {
      // 更新最后动作时间戳
      setLastActionTimestamp(latestAction.timestamp);
      
      // 强制重置所有项目的高亮状态
      setForceResetCounter(prev => prev + 1);
      
      // 让容器滚动到底部
      if (containerRef.current && !compactMode) {
        setTimeout(() => {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }, 100);
      }
    }
  }, [latestAction, lastActionTimestamp, compactMode]);

  // 辅助监听器，确保在actions变化时也会滚动
  useEffect(() => {
    if (containerRef.current && !compactMode) {
      setTimeout(() => {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }, 100);
    }
  }, [actions, compactMode]);

  // 计算所有动作的总数
  const totalActions = Object.values(actions).reduce(
    (total, streetActions) => total + streetActions.length, 
    0
  );

  // 修改每个动作的显示
  const getDisplayAction = (action, street, index, streetActions) => {
    // 检查是否有之前的加注动作
    const hasPriorRaise = streetActions.slice(0, index).some(a => a.action === 'raise' || a.action === 'bet');
    
    // 确定显示的动作文本
    if (street === 'preflop') {
      if (action.action === 'raise' && !hasPriorRaise) {
        return 'open';
      } else if (action.action === 'call' && !hasPriorRaise) {
        return 'limp';
      }
    }
    
    return action.action;
  };

  return (
    <div className="space-y-6">
      {Object.entries(actions).map(([street, streetActions]) => {
        if (streetActions.length === 0) return null;
        
        return (
          <div key={street} className="space-y-3">
            <h3 className="text-xl font-bold text-blue-600 border-b border-blue-200 pb-2">
              {t(`poker.streets.${street.toLowerCase()}`)}:
            </h3>
            <div className="space-y-2">
              {streetActions.map((action, index) => {
                // 显示专业术语
                const displayAction = getDisplayAction(action, street, index, streetActions);
                
                // 创建一个新的动作对象，包含正确的显示动作
                const actionWithDisplay = {
                  ...action,
                  action: displayAction,
                };
                
                return (
                  <ActionItem 
                    key={`${street}-${index}-${action.timestamp || index}`}
                    action={actionWithDisplay}
                    isLatest={latestAction && action.timestamp === latestAction.timestamp}
                    forceReset={forceResetCounter}
                    street={street}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
      {gameOver && (
        <div className="mt-4 px-4 py-3 bg-green-100 text-green-800 rounded-lg font-semibold">
          {t('poker.sections.gameOver')}
        </div>
      )}
    </div>
  );
};

export default ActionHistory;